// @flow
import React, { useEffect } from "react";
import { Form } from "semantic-ui-react";
import { Field, formValues, type FormProps, type FieldProps } from "redux-form";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import Styles from "../../ChangeServices/submodules/Old_Edit/components/Customer/SharedStyles.module.scss";
import FormLabel from "Commons/Components/FormComponents/components/FormLabelWithDivider";
import { isRequired } from "../../ChangeServices/submodules/Old_Edit/misc/Validators";
import { InputField } from "../../ChangeServices/submodules/Old_Edit/components/Fields";
import { isPostalCode, isPresent } from "Commons/Utilities";
import { fetchStreets } from "redux/actions/street";
import { getStreets } from "redux/sagas/selectors";
import { containsMin1Digit } from "Commons/Utilities/validators/changeService";

type Props = {
  fields: Dict,
  t: typeof TFunction,
  mobileChangeService: boolean,
  noLabel?: boolean,
  widthStreetName?: number,
  widthStreetNumber?: number,
  divider: boolean,
  initialValues: Dict,
  form: string,
  change: $PropertyType<FormProps, "change">,
};
export const StreetAndStreetNumber = ({
  fields,
  t,
  mobileChangeService,
  noLabel,
  widthStreetName = 8,
  widthStreetNumber = 2,
  divider,
  initialValues = {},
  form,
  change,
}: Props) => {
  return (
    <Form.Group id={Styles["inline"]}>
      {!noLabel && (
        <FormLabel
          divider={divider}
          id={fields.street}
          title={t("common:streetAndStreetNumber")}
          inline
          tooltip={t("mobile.customer.addressDetails.addressInfo")}
          secondary
        />
      )}
      <Street
        fields={fields}
        form={form}
        width={widthStreetName}
        mobileChangeService={mobileChangeService}
        initialValues={initialValues}
        change={change}
      />
      <Field
        name={fields.streetNumber}
        validate={[isRequired, containsMin1Digit]}
        component={InputField}
        props={{
          id: fields.streetNumber,
          placeholder: t("common:numberAbbrv"),
          width: widthStreetNumber,
          showErrorMessage: false,
          className: classnames({
            [Styles.three]: mobileChangeService,
          }),
        }}
      />
    </Form.Group>
  );
};

const StreetSelect = (
  props: {|
    t: typeof TFunction,
    options: {| value: string, text: string |},
    loading: boolean,
  |} & FieldProps
) => {
  const { t, options, loading } = props;

  return (
    <Form.Select
      selection
      search
      loading={loading}
      searchInput={{
        id: "street",
        type: "search",
        autoComplete: "off",
      }}
      placeholder={t("street")}
      options={options}
      value={props.input.value}
      error={props.meta.invalid && props.meta.touched && props.meta.error}
      onChange={(_, data) => {
        props.input.onChange(data.value);
      }}
    />
  );
};

type StreetFieldProps = {|
  width?: number,
  fields: Dict,
  form: string,
  mobileChangeService?: boolean,
  initialValues: Dict,
  change: $PropertyType<FormProps, "change">,
  postalCode: ?string,
  locationMainName: ?string,
  locationName: ?string,
  street: ?string,
|};

let StreetField = (props: StreetFieldProps) => {
  const {
    width,
    fields,
    form,
    mobileChangeService = false,
    initialValues,
    change,
    postalCode,
    locationMainName,
    locationName,
    street,
  } = props;
  const { t } = useTranslation("common");
  const location = locationMainName || locationName;

  const dispatch = useDispatch();

  useEffect(() => {
    if (isPostalCode(postalCode) && isPresent(postalCode) && isPresent(location)) {
      dispatch(fetchStreets(postalCode, location, form, fields.postalCode));
    }
  }, [postalCode, location, dispatch, form, fields.postalCode]);

  const streets = useSelector((state) => {
    const currentStreets = getStreets(state, postalCode, location);
    const defaultOptions = [
      {
        Name: "",
      },
    ];

    return currentStreets ? currentStreets : defaultOptions;
  });

  useEffect(() => {
    // Autofill street selector if there is only one street.
    if (
      postalCode &&
      postalCode.length === 5 &&
      postalCode !== initialValues.postalcode &&
      streets[0].Name !== "" &&
      streets.length === 1 &&
      streets[0].Name !== street
    ) {
      change(fields.street, streets[0].Name);
    }
  }, [streets, postalCode, street, fields, initialValues.postalcode, change]);

  const options = streets.map((street) => {
    return {
      value: street.Name,
      text: street.Name,
    };
  });

  const loading = useSelector((state) => state.streets.isFetching);

  return (
    <Form.Field
      width={width}
      className={classnames({
        [Styles.seven]: mobileChangeService,
      })}
    >
      <Field name={fields.street} validate={[isRequired]} component={StreetSelect} props={{ options, loading, t }} />
    </Form.Field>
  );
};

export const Street = formValues((props) => ({
  postalCode: props.fields.postalCode,
  street: props.fields.street,
  locationMainName: props.fields.locationMainName,
  locationName: props.fields.locationName,
}))(StreetField);
